'use client';

import { useEffect, useMemo, useRef } from 'react';
import gsap from 'gsap/all';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Container, Text, Image } from '@weareconceptstudio/core';

//* Helpers
import { config } from '@/helpers';

//* Style
import BenefitsStyle from './style';

//* Component
import StyledQuotedText from '@/components/StyledQuotedText';
import Button from '@/components/Button';
import TitleAnim from '@/modules/_animation/Title';

gsap.registerPlugin(ScrollTrigger);

const Benefits = ({ title, items }) => {
	//! Ref
	const triggerRef = useRef();
	const styleRef = useRef();
	const wrapperButtonRef = useRef();

	//! News Item
	const dataBenefitsImg = useMemo(() => {
		return items?.map((item, i) => (
			<div
				key={i}
				className={`col-4 col-m-12 ${!item.image2 ? 'img-item' : ''}`}>
				{item.image2 ? (
					<div className='image-wrapper'>
						<div className='image-type-2'>
							<Image
								src={item.image.src}
								alt={item.image.alt}
							/>
						</div>
						<div className='image-type-3'>
							<Image
								src={item.image2.src}
								alt={item.image2.alt}
							/>
						</div>
					</div>
				) : (
					<div className='image-type-1'>
						<Image
							src={item.image.src}
							alt={item.image.alt}
						/>
					</div>
				)}
			</div>
		));
	}, [items]);

	const dataBenefits = useMemo(() => {
		return items?.map((item, i) => (
			<div
				key={i}
				className='col-4 col-m-12'>
				<div className='benefit-info-wrapper'>
					<Text className='h5 font-larken-regular benefit-item-title'>{item.title}</Text>

					<Text className='p1 font-fss-light benefit-item-desc'>{item.description}</Text>
				</div>
			</div>
		));
	}, [items]);

	useEffect(() => {
		if (styleRef.current) {
			triggerRef.current = ScrollTrigger.create({
				start: 'top bottom',
				end: 'top top',
				trigger: styleRef.current,
				// markers: true,
				onEnter: () => {
					gsap.fromTo(wrapperButtonRef.current, { scale: 0 }, { scale: 1, duration: 0.6, ease: 'power2.inOut', delay: 0.2 });

					const allImage = styleRef.current?.querySelectorAll('img');
					if (allImage?.length) {
						const delta = Math.floor(allImage.length / 2);
						[...allImage]
							.slice(0, delta)
							.reverse()
							.forEach((item, index, arr) => {
								gsap.fromTo(item, { rotate: -8, y: '40%' }, { rotate: 0, duration: 1, ease: 'power2.out', delay: `${(arr.length - 1 - index) * 0.15}`, y: 0 });
							});

						[...allImage].slice(delta).forEach((item, index, arr) => {
							gsap.fromTo(
								item,
								{ rotate: 8, y: '40%' },
								{ rotate: 0, duration: 1, ease: 'power2.out', y: 0, delay: `${(index + delta == arr.length - 1 ? delta - 2 : index + delta) * 0.15}` }
							);
						});
					}
					const allBenefit = styleRef?.current?.querySelectorAll('.benefit-info-wrapper');
					if (allBenefit?.length) {
						allBenefit.forEach((element, i, arr) => {
							gsap.fromTo(element, { opacity: 0, y: '50%' }, { opacity: 1, y: 0, duration: 1, ease: 'power2.out', delay: `${i == arr.length - 1 ? 0.25 * (i + 0.25 - 1) : 0.25 * i}` });
						});
					}
				},
				onLeaveBack: () => {
					const allImage = styleRef.current?.querySelectorAll('img');
					if (allImage?.length) {
						const delta = Math.floor(allImage.length / 2);
						[...allImage]
							.slice(0, delta)
							.reverse()
							.map((item, index) => {
								gsap.killTweensOf(item);
								gsap.set(item, { rotate: -15 * (index + 1), y: '20%' });
							});
						[...allImage].slice(delta).map((item, index) => {
							gsap.killTweensOf(item);
							gsap.set(item, { rotate: 15 * (index + 1), y: '20%' });
						});
					}

					const allBenefit = styleRef?.current?.querySelectorAll('.benefit-info-wrapper');
					if (allBenefit?.length) {
						allBenefit.forEach((element, i) => {
							gsap.killTweensOf(element);
							gsap.set(element, { opacity: 0, y: '50%' });
						});
					}
					gsap.killTweensOf([wrapperButtonRef.current]);
					gsap.set(wrapperButtonRef.current, { scale: 0 });
				},
			});
		}
		return () => {
			if (triggerRef.current) {
				triggerRef.current.kill();
			}
		};
	});

	return (
		<BenefitsStyle ref={styleRef}>
			<Container
				isSection
				row>
				<div className={'col-12'}>
					{items?.length > 0 && (
						<>
							<div className='wrapper-titles'>
								<div>
									<TitleAnim
										textCenter={false}
										trigger={'bottom'}
										animRefTrigger={styleRef}
										load={true}>
										<StyledQuotedText text={title} />
									</TitleAnim>
								</div>
								<div
									ref={wrapperButtonRef}
									className='wrap-button'>
									<Button
										url={`${config.routes.benefits.path}`}
										text={'viewAll'}
										className={'show-more-btn'}
									/>
								</div>
							</div>

							<div className={'wrapper-benefits row'}>
								{dataBenefitsImg}
								{dataBenefits}
							</div>
						</>
					)}
				</div>
			</Container>
		</BenefitsStyle>
	);
};

export default Benefits;
